'use strict';
import React, { createContext, useContext, useState, useCallback } from 'react';

const LoadingContext = createContext(null);

/**
 * Global loading state provider
 * Prevents multiple spinners from appearing simultaneously
 */
export const LoadingProvider = ({ children }) => {
	const [loadingStates, setLoadingStates] = useState(new Set());

	const setLoading = useCallback((key, isLoading) => {
		setLoadingStates(prev => {
			const next = new Set(prev);
			if (isLoading) {
				next.add(key);
			} else {
				next.delete(key);
			}
			return next;
		});
	}, []);

	const isLoading = loadingStates.size > 0;

	return (
		<LoadingContext.Provider value={{ isLoading, setLoading }}>
			{children}
		</LoadingContext.Provider>
	);
};

/**
 * Hook to access loading context
 */
export const useLoading = () => {
	const context = useContext(LoadingContext);
	if (!context) {
		throw new Error('useLoading must be used within LoadingProvider');
	}
	return context;
};

/**
 * Hook to manage a component's loading state
 * Automatically registers/unregisters loading state
 */
export const useLoadingState = (key, isLoading) => {
	const { setLoading } = useLoading();

	React.useEffect(() => {
		setLoading(key, isLoading);
		return () => setLoading(key, false);
	}, [key, isLoading, setLoading]);
};

